import React from "react";

/**
 * 💀 SKELETON DA HOME
 * Espelha a estrutura real da Home (mesmas classes, paddings e alturas) para que,
 * ao terminar o carregamento, o conteúdo apareça no lugar exato do esqueleto —
 * sem a página esticar/encolher.
 *
 * Regras: só brilho horizontal (background-position), nunca transform/height,
 * assim nada "se mexe" durante o loading.
 */

// 📏 Barra genérica de placeholder
const Bar = ({ w = "100%", h = 14, r = 6, className = "", style = {} }) => (
  <div
    className={`hs-bar ${className}`}
    style={{ width: w, height: h, borderRadius: r, ...style }}
  />
);

// 📊 Card de progresso (Despesa / RH / Consolidado)
const ProgressCardSkeleton = () => (
  <div className="p-3 bg-white rounded-3 shadow-sm border h-100">
    <div className="d-flex align-items-center justify-content-between mb-2">
      <Bar w="60%" h={17} />
      <Bar w="34px" h={17} />
    </div>
    <Bar h={6} r={10} />
    <div className="d-flex justify-content-between mt-2">
      <Bar w="42%" h={12} />
      <Bar w="42%" h={12} />
    </div>
  </div>
);

// 💳 Card de KPI
const KpiCardSkeleton = () => (
  <div className="col-lg-4 col-md-6 col-12">
    <div className="card border-0 shadow-sm h-100" style={{ minHeight: "160px" }}>
      <div className="card-body p-4 d-flex flex-column justify-content-between">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <Bar w="50px" h={50} r={12} />
        </div>
        <div className="flex-grow-1">
          <Bar w="55%" h={15} />
          <div className="mb-2 mt-2 d-flex align-items-center" style={{ height: "34px" }}>
            <Bar w="70%" h={28} r={8} />
          </div>
          <Bar w="35%" h={12} />
        </div>
      </div>
      <div
        className="card-footer border-0 p-0 hs-bar"
        style={{ height: "4px", borderRadius: "0 0 8px 8px" }}
      />
    </div>
  </div>
);

// 🧠 Linha de insight econômico
const InsightRowSkeleton = () => (
  <div className="col-12">
    <div
      className="border rounded-3 p-3"
      style={{ background: "rgba(255,255,255,0.9)" }}
    >
      <div className="d-flex align-items-start">
        <Bar w="28px" h={28} r={8} className="me-3" />
        <div className="flex-grow-1">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <Bar w="45%" h={16} />
            <Bar w="80px" h={16} r={10} />
          </div>
          <Bar h={13} className="mb-1" />
          <Bar w="80%" h={13} />
          <div className="mt-2">
            <Bar w="60%" h={12} />
          </div>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <Bar w="30%" h={11} />
            <Bar w="25%" h={11} />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// 📈 Item da lista "Todos os Indicadores"
const IndicatorRowSkeleton = () => (
  <div className="col-12 mb-2">
    <div className="d-flex align-items-center justify-content-between p-2 bg-light rounded">
      <div className="d-flex align-items-center">
        <Bar w="16px" h={16} r={4} className="me-2" />
        <div>
          <Bar w="90px" h={13} />
          <Bar w="150px" h={10} className="mt-1" />
        </div>
      </div>
      <Bar w="42px" h={13} />
    </div>
  </div>
);

const HomeSkeleton = ({ hasRHAccess = false }) => (
  <div
    className="min-vh-100"
    style={{
      background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
    }}
    aria-busy="true"
    aria-live="polite"
  >
    <span className="visually-hidden">Carregando painel de orçamento...</span>

    <div className="container-fluid p-4">
      {/* Header — 3 linhas: saudação | contexto + perfil | cards */}
      <div className="mb-4">
        {/* LINHA 1 — saudação */}
        <Bar w="320px" h={30} className="mb-3" style={{ maxWidth: "100%" }} />

        {/* LINHA 2 — faixa "Visualizando" (compacta) + data, contexto e avatar */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
          <div
            className="border-0 shadow-sm rounded-3 d-flex flex-wrap align-items-center gap-3 p-3"
            style={{
              background: "rgba(207, 232, 252, 0.55)",
              width: "fit-content",
            }}
          >
            <Bar w="150px" h={22} />
            <Bar w="130px" h={26} r={20} />
            <Bar w="210px" h={26} r={20} />
            <Bar w="230px" h={24} r={20} />
          </div>

          {/* Data / Contexto / Avatar — três blocos separados, como na Home */}
          <div className="d-flex align-items-center gap-3">
            <div className="text-center">
              <Bar w="40px" h={13} className="mx-auto mb-1" />
              <Bar w="80px" h={17} />
            </div>
            <Bar w="104px" h={62} r={8} />
            <Bar w="60px" h={60} r={999} />
          </div>
        </div>

        {/* LINHA 3 — cards de progresso */}
        <div className="row g-3">
          <div className={hasRHAccess ? "col-md-4" : "col-md-6"}>
            <ProgressCardSkeleton />
          </div>
          {hasRHAccess && (
            <div className="col-md-4">
              <ProgressCardSkeleton />
            </div>
          )}
          <div className={hasRHAccess ? "col-md-4" : "col-md-6"}>
            <ProgressCardSkeleton />
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="row g-4 mb-4">
        <KpiCardSkeleton />
        <KpiCardSkeleton />
        <KpiCardSkeleton />
      </div>

      <div className="row g-4 pb-4">
        {/* Insights */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm home-panel">
            <div className="card-header bg-transparent border-0 pt-4">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <Bar w="40px" h={40} r={12} className="me-3" />
                  <div>
                    <Bar w="280px" h={20} className="mb-2" />
                    <Bar w="330px" h={13} />
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Bar w="120px" h={24} r={10} />
                  <Bar w="38px" h={31} r={6} />
                </div>
              </div>
            </div>
            <div className="card-body d-flex flex-column">
              <div className="row g-3">
                <InsightRowSkeleton />
                <InsightRowSkeleton />
                <InsightRowSkeleton />
                <InsightRowSkeleton />
              </div>

              {/* Rodapé com Fonte */}
              <div className="mt-auto pt-3 border-top">
                <div className="d-flex align-items-center justify-content-between">
                  <Bar w="34%" h={13} />
                  <Bar w="20%" h={13} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Índices Econômicos */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm home-panel">
            <div className="card-header bg-transparent border-0 pt-4">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <Bar w="40px" h={40} r={12} className="me-3" />
                  <div>
                    <Bar w="210px" h={20} className="mb-2" />
                    <Bar w="230px" h={13} />
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Bar w="60px" h={24} r={10} />
                </div>
              </div>
            </div>
            <div className="card-body d-flex flex-column">
              {/* Grade 2x2 dos principais */}
              <div className="row g-3 mb-4">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="col-6">
                    <Bar h={90} r={12} />
                  </div>
                ))}
              </div>

              {/* Lista de indicadores */}
              <div className="border-top pt-3">
                <Bar w="150px" h={16} className="mb-3" />
                <div className="row g-2">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => (
                    <IndicatorRowSkeleton key={i} />
                  ))}
                </div>
              </div>

              {/* Resumo estatístico */}
              <div className="mt-4 pt-3 border-top">
                <div className="row g-2">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="col-4">
                      <Bar h={54} r={8} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Rodapé */}
              <div className="mt-auto pt-3 border-top">
                <div className="d-flex align-items-center justify-content-between">
                  <Bar w="45%" h={13} />
                  <Bar w="25%" h={13} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <style
      dangerouslySetInnerHTML={{
        __html: `
        .hs-bar {
          background-color: #e4e9ef;
          background-image: linear-gradient(
            90deg,
            rgba(255,255,255,0) 0%,
            rgba(255,255,255,0.65) 50%,
            rgba(255,255,255,0) 100%
          );
          background-repeat: no-repeat;
          background-size: 220px 100%;
          background-position: -220px 0;
          animation: hs-shimmer 1.4s linear infinite;
          border-radius: 6px;
          flex-shrink: 0;
        }

        @keyframes hs-shimmer {
          to { background-position: calc(100% + 220px) 0; }
        }

        @media (prefers-reduced-motion: reduce) {
          .hs-bar { animation: none; }
        }
      `,
      }}
    />
  </div>
);

export default HomeSkeleton;
